import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Create an axios instance that auto-adds Bearer token
function createApi(tokenRef) {
  const instance = axios.create({ baseURL: API });
  instance.interceptors.request.use((config) => {
    const token = tokenRef.current;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  return instance;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = { current: localStorage.getItem("sl_token") };

  const api = useMemo(() => createApi(tokenRef), []); // eslint-disable-line react-hooks/exhaustive-deps

  const setToken = (token) => {
    tokenRef.current = token;
    if (token) {
      localStorage.setItem("sl_token", token);
    } else {
      localStorage.removeItem("sl_token");
    }
  };

  const checkAuth = useCallback(async () => {
    const saved = localStorage.getItem("sl_token");
    if (!saved) { setLoading(false); return; }
    tokenRef.current = saved;
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setToken(res.data.session_token);
    setUser(res.data);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    setToken(res.data.session_token);
    setUser(res.data);
    return res.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const googleCallback = async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId });
    setToken(res.data.session_token);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch { /* ignore */ }
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, loginWithGoogle, googleCallback, logout, checkAuth, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
